import {
	ConstrainMode,
	IDetailsListStyles,
	SelectionMode,
} from 'components/FluentUICompat';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
	useGetVehicleProjectParagraphsNextLazyQuery,
	useGetVehicleProjectParagraphsSortOrderLazyQuery,
} from 'features/VehicleProjects/hooks/AssignedParagraphsInfinity.generated';
import {
	InfinityList,
	InfinityListColumn,
	InfinityListColumnMetaData,
	funcDecodeHex24ToDocId,
	InfinityListView,
} from 'components/InfinityList';
import type {EntityListColumn} from 'components';
import {
	queryStringMongoArraySTD,
	queryStringMongoDateSTD,
	useInfinityListStandardHooks,
} from 'components/InfinityList/InfinitListStandardHooks';
import {useGetRegDocParagraphIdsLazyQuery} from '../hooks/RegDocParagraphsInfinity.generated';
import {useTranslation} from 'react-i18next';
import {useParagraphsContext} from '../components/DocumentDetails/ParagraphsContext';
import {useEntityContext} from 'components/EntityPage/EntityContext';
import {useUserContext} from 'authentication/UserContext';
import useQuestionsPermissions from 'components/Faq/hooks/useQuestionsPermissions';
import {useParagraphListColumns} from '../components/DocumentDetails/ParagraphListBaseColumns/hooks/useParagraphListColumns';
import {getCreateParagraphFieldWithDefaults} from '../components/DocumentDetails/ParagraphListBaseColumns/ParagraphListBaseColumns';
import {
	renderAttachments,
	renderRichtext,
} from 'components/EntityList/ColumnRenderers';
import {DATE_TYPE} from 'components/EntityList/EntityUtils';
import type {
	AttachmentRef,
	FilterOption,
	KeywordAssignment,
	RegulatoryDocumentParagraph,
	VexClusterAssignment,
} from 'types';
import {ParagraphsListKeywordAssignments} from '../components/DocumentDetails/ParagraphsListKeywordAssignments';

const initialSortField = 'p-paragraph';
const initialSortOrder = 'ASC';
const intBatchSize = 50;
const HEX24_REGEX = /^[a-f0-9]{24}$/i;

const hideNativeHorizontalScrollbarStyles: Partial<IDetailsListStyles> = {
	root: {
		selectors: {
			'& .ms-DetailsList-contentWrapper': {
				msOverflowStyle: 'none',
				scrollbarWidth: 'none',
				selectors: {
					'&::-webkit-scrollbar': {
						display: 'none',
					},
				},
			},
		},
	},
};

const decodeToHex24Id = (value: string) => {
	if (HEX24_REGEX.test(value)) {
		return value;
	}

	const decodedValue = funcDecodeHex24ToDocId(value);
	return HEX24_REGEX.test(decodedValue) ? decodedValue : value;
};

const toInfinityFilterType = (
	column: Pick<EntityListColumn, 'filterable' | 'dataType'>,
): InfinityListColumn['filterType'] | undefined => {
	if (!column.filterable) {
		return undefined;
	}

	if (column.dataType === DATE_TYPE) {
		return 'Date';
	}

	return 'Array';
};

const toInfinityListColumn = (column: EntityListColumn) => {
	const filterType = toInfinityFilterType(column);

	return {
		key: column.key,
		name: column.name,
		fieldName: column.fieldName,
		minWidth: column.minWidth,
		maxWidth: column.maxWidth,
		isResizable: column.isResizable,
		isMultiline: column.isMultiline,
		isIconOnly: column.isIconOnly,
		iconName: column.iconName,
		onRender: column.onRender,
		className: column.className,
		hidden: column.hidden,
		sortable: column.sortable,
		filterable: column.filterable,
		filterOnFilter:
			filterType === 'Array' ? column.filterOnFilter ?? true : undefined,
		filterType,
		tooltipHostProps: column.tooltipHostProps,
	};
};

const toFilterOption = (value: unknown, column: EntityListColumn) => {
	if (value === undefined || value === null) {
		return undefined;
	}

	const getKey = column.getFilterKey ?? ((item: any) => item?.id ?? item);
	const getLabel =
		column.getFilterLabel ?? ((item: any) => item?.name ?? String(item));

	const optionKey = getKey(value);
	if (optionKey === undefined || optionKey === null) {
		return undefined;
	}

	const nameValue = getLabel(value);
	const name = String(nameValue ?? optionKey).trim();
	if (name === '') {
		return undefined;
	}

	return {optionKey: String(optionKey), name};
};

const getColumnFilterValues = (
	column: EntityListColumn,
	items: RegulatoryDocumentParagraph[],
): FilterOption[] => {
	const optionsByKey = new Map<string, FilterOption>();

	for (const item of items) {
		const value = column.getFieldValue
			? column.getFieldValue(item)
			: item[column.fieldName as keyof RegulatoryDocumentParagraph];

		if (Array.isArray(value)) {
			for (const entry of value) {
				const option = toFilterOption(entry, column);
				if (option) {
					optionsByKey.set(option.optionKey, option);
				}
			}
			continue;
		}

		const option = toFilterOption(value, column);
		if (option) {
			optionsByKey.set(option.optionKey, option);
		}
	}

	return [...optionsByKey.values()].sort((a, b) =>
		a.name.localeCompare(b.name),
	);
};

export type RegDocDetailsPageParagraphsListProps = Readonly<{
	regulatoryDocumentId: string;
	tocFilteredParagraphIds?: string[];
	allParagraphsInScope?: RegulatoryDocumentParagraph[];
}>;

export function RegDocDetailsPageParagraphsList({
	regulatoryDocumentId,
	tocFilteredParagraphIds = [],
	allParagraphsInScope,
}: RegDocDetailsPageParagraphsListProps) {
	const {selectedItems} = useEntityContext<RegulatoryDocumentParagraph>();
	const {setSelectedParagraphs} =
		useParagraphsContext<RegulatoryDocumentParagraph>();

	const arrDocumentRef = useRef<RegulatoryDocumentParagraph[]>([]);
	const totalCountRef = useRef(0);
	const appliedSelectionSignatureRef = useRef('');

	useEffect(() => {
		const loadedCount = arrDocumentRef.current.length;
		const isAllSelected =
			(selectedItems?.length ?? 0) > 0 &&
			selectedItems?.length === loadedCount &&
			totalCountRef.current > loadedCount &&
			allParagraphsInScope !== undefined &&
			allParagraphsInScope.length > 0;

		const paragraphsToSet =
			isAllSelected && allParagraphsInScope
				? allParagraphsInScope
				: selectedItems ?? [];
		const selectionSignature = paragraphsToSet.map(p => p.id).join('|');

		if (appliedSelectionSignatureRef.current === selectionSignature) {
			return;
		}

		appliedSelectionSignatureRef.current = selectionSignature;
		setSelectedParagraphs(paragraphsToSet);
	}, [selectedItems, setSelectedParagraphs, allParagraphsInScope]);

	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'ParagraphsList',
	});
	const {t: tt} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'TooltipsText',
	});
	const {t: tEnum} = useTranslation('common/enums');
	const {shouldShowFaq} = useQuestionsPermissions();
	const {isRegulationReader, isReader, myKeywords} = useUserContext();

	const navigate = useNavigate();

	const [getRegDocParagraphIds] = useGetRegDocParagraphIdsLazyQuery();

	const [getParagraphsNext, {loading: loadingDocuments}] =
		useGetVehicleProjectParagraphsNextLazyQuery();

	const [getSortOrder] = useGetVehicleProjectParagraphsSortOrderLazyQuery();

	const views: InfinityListView[] = useMemo(() => [], []);
	const initialView = 'STD';
	const [totalCount, setTotalCount] = useState(0);

	const possibleKeywordAssignmentColumns: EntityListColumn[] = useMemo(() => {
		if (isReader) {
			return [];
		}

		return [
			{
				name: t('KeywordAssignments'),
				key: 'keywordAssignments',
				fieldName: 'keywordAssignments',
				minWidth: 180,
				isResizable: true,
				filterable: true,
				getFilterKey: (vca: VexClusterAssignment) => vca.keywordStatus,
				getFilterLabel: (vca: VexClusterAssignment) => t(vca.keywordStatus),
				getTranformMatrix: (ka: KeywordAssignment[]) =>
					ka.flatMap(x => x.vexClusterAssignments),
				onRender: item => (
					<ParagraphsListKeywordAssignments
						keywordAssignments={item.keywordAssignments ?? []}
						limit={2}
					/>
				),
			},
		];
	}, [t, isReader]);

	const getSummaryField = useMemo<EntityListColumn>(() => {
		const createField = getCreateParagraphFieldWithDefaults(true);
		return createField({
			key: 'p-summary',
			name: t('Summary'),
			fieldName: 'summary',
			minWidth: 150,
			filterable: true,
			getFieldValue(item) {
				const summary = (item.summary ?? '')
					.replace(/<[^>]{0,10000}>/g, '')
					.trim();
				if (summary === '') {
					return null;
				}
				return summary;
			},
			onRender: renderRichtext(),
		});
	}, [t]);

	const paragraphColumns = useParagraphListColumns({
		t,
		tt,
		tEnum,
		filterable: true,
		shouldShowFaq,
		myKeywords,
		isReqifz: false,
	});

	const visibleColumns: EntityListColumn[] = useMemo(() => {
		const hiddenForRegulationReader = new Set([
			'keywordAssignments',
			'p-tags',
			'p-requirements',
			'attachments',
			'p-summary',
		]);
		const cols: EntityListColumn[] = [
			...possibleKeywordAssignmentColumns,
			...paragraphColumns,
			{
				name: t('Attachments'),
				key: 'attachments',
				isIconOnly: true,
				iconName: 'Attach',
				fieldName: 'attachments',
				minWidth: 16,
				maxWidth: 16,
				filterable: true,
				getFilterKey(attach: AttachmentRef) {
					return attach.file?.fileName;
				},
				getFilterLabel(attach: AttachmentRef) {
					return attach.file?.fileName;
				},
				onRender: renderAttachments(),
			},
			getSummaryField,
		];

		return isRegulationReader
			? cols.filter(col => hiddenForRegulationReader.has(col.key) === false)
			: cols;
	}, [
		possibleKeywordAssignmentColumns,
		paragraphColumns,
		getSummaryField,
		isRegulationReader,
		t,
	]);

	const columns: InfinityListColumn[] = useMemo(
		() => visibleColumns.map(toInfinityListColumn),
		[visibleColumns],
	);

	const columnMetaData: InfinityListColumnMetaData[] = useMemo(() => {
		return columns
			.filter(
				column => column.filterType === 'Array' || column.filterType === 'Date',
			)
			.map(column => {
				const mongoFieldName = column.fieldName ?? column.key;

				return {
					key: column.key,
					mongoDBFn:
						column.filterType === 'Date'
							? queryStringMongoDateSTD(mongoFieldName)
							: queryStringMongoArraySTD(mongoFieldName),
				};
			});
	}, [columns]);

	const queryDocumentsDB = async (
		_query: unknown,
		_sortField: string,
		_sortOrder: string,
	) => {
		const {data: idsData} = await getRegDocParagraphIds({
			variables: {regulatoryDocumentId},
		});

		const tocFilteredIdsSet = new Set(
			tocFilteredParagraphIds.map(decodeToHex24Id),
		);

		const allParaIds = (idsData?.regulatoryDocument?.paragraphs ?? [])
			.filter(
				p =>
					tocFilteredParagraphIds.length === 0 ||
					tocFilteredIdsSet.has(decodeToHex24Id(p.id)),
			)
			.map(p => ({
				regDocId: decodeToHex24Id(regulatoryDocumentId),
				paragraphId: decodeToHex24Id(p.id),
			}));

		// Use plain paragraph IDs as list IDs to keep pagination/sorting IDs
		// aligned with loaded document IDs.
		const arrIDsNew = allParaIds.map(p => p.paragraphId);
		setTotalCount(arrIDsNew.length);

		const firstBatchIds = allParaIds.slice(0, intBatchSize);
		const {data: batchData} = await getParagraphsNext({
			variables: {nextParaArgs: {preFilteredParagraphIds: firstBatchIds}},
		});

		const arrDocumentNext = (batchData?.paragraphsNext ??
			[]) as RegulatoryDocumentParagraph[];

		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const preFilteredParagraphIds = preFilteredIds.map(paragraphId => ({
			regDocId: decodeToHex24Id(regulatoryDocumentId),
			paragraphId: decodeToHex24Id(paragraphId),
		}));

		const {data} = await getParagraphsNext({
			variables: {
				nextParaArgs: {
					preFilteredParagraphIds,
				},
			},
		});
		return (data?.paragraphsNext ?? []) as RegulatoryDocumentParagraph[];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const preFilteredParagraphIds = preFilteredIds.map(paragraphId => ({
			regDocId: decodeToHex24Id(regulatoryDocumentId),
			paragraphId: decodeToHex24Id(paragraphId),
		}));

		const {data} = await getSortOrder({
			variables: {
				sortParaArgs: {
					sortField,
					preFilteredParagraphIds,
				},
			},
		});
		return (data?.paragraphsSortOrder ?? []).map(p =>
			decodeToHex24Id(p.paragraphId),
		);
	};

	const combineViewAndUserFilterQuery = (
		queryUser: unknown,
		_views: InfinityListView[],
		_viewKey?: string,
	) => queryUser;

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds: [],
		generateLink: false,
		columns,
		columnMetaData,
		combineViewAndUserFilterQuery,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
	});

	const columnFilterOptions = useMemo(() => {
		const items = allParagraphsInScope ?? arrDocument;

		return visibleColumns
			.filter(col => toInfinityFilterType(col) === 'Array')
			.map(col => ({
				key: col.key,
				fieldValuesUnique: getColumnFilterValues(col, items),
			}));
	}, [allParagraphsInScope, arrDocument, visibleColumns]);

	const handleViewingItem = (item: RegulatoryDocumentParagraph) => {
		navigate({pathname: `/requirements/${item.id}`});
	};

	// Keep refs in sync with live values so the selection effect always sees latest counts
	arrDocumentRef.current = arrDocument;
	totalCountRef.current = totalCount;

	const createHref = (_item: unknown) => `/requirements/`;

	return (
		<InfinityList
			pageName={''}
			items={arrDocument}
			sticky={false}
			columns={columns}
			columnMetaData={columnMetaData}
			columnFilterOptions={columnFilterOptions}
			constrainMode={ConstrainMode.horizontalConstrained}
			selectionMode={SelectionMode.multiple}
			stillLoading={loadingDocuments}
			styles={hideNativeHorizontalScrollbarStyles}
			initalFilters={linksFilters}
			initialSortField={initialSortField}
			initialSortOrder={initialSortOrder}
			initialView={initialView}
			loadingIndicatorPlacement='pageBottomCenter'
			keyPrefix={keyPrefix}
			firstRender={firstRenderSTD}
			loadItemsNext={loadItemsNextSTD}
			applyFilter={applyFilterSTD}
			applySort={applySortSTD}
			handleInvokeItem={handleViewingItem}
			createHref={createHref}
			numberOfElements={totalCount}
			showHeader={false}
			views={views}
			advancedFilterShow={false}
		/>
	);
}
